import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PatchForm from "./components/PatchForm";
import PatchDetails from "../PatchDetails";
import { useLanguage } from "../../contexts/LanguageContext";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { BsArrowLeftRight } from "react-icons/bs";
import { MdRestartAlt } from "react-icons/md";

const API_URL = process.env.REACT_APP_API_URL;

const AddPatch = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [hasChange, setHasChange] = useState(false);
  const [leftWidth, setLeftWidth] = useState(50);
  const [loading, setLoading] = useState(false);
  const [patch, setPatch] = useState({
    yamaVersiyonu: "",
    title: "",
    subtitle: "",
    summary: "",
    tarih: new Date().toISOString(),
    image: null,
    sections: [
      {
        id: uuidv4(),
        title: "",
        content: "",
        images: [],
        table: { columns: [], rows: [] },
        thumbnail: [],
        imageFile: null,
        thumbnailFile: null,
        bossUpdate: false,
      },
    ],
  });

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChange) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChange]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftWidth;

    const onMouseMove = (e) => {
      const delta = e.clientX - startX;
      const newWidth =
        (((startWidth / 100) * window.innerWidth + delta) / window.innerWidth) *
        100;

      if (newWidth > 1 && newWidth < 99) {
        setLeftWidth(newWidth);
      }
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  const savePatch = async (newPatch) => {
    try {
      const formData = new FormData();

      const newSections = await Promise.all(
        newPatch.sections.map(async (s, i) => {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = s.content;

          const imgTags = tempDiv.querySelectorAll("img");

          for (let j = 0; j < imgTags.length; j++) {
            const img = imgTags[j];
            if (img.src.startsWith("blob")) {
              const res = await fetch(img.src);
              const blob = await res.blob();
              const mime = blob.type.split("/")[1].replace("+xml", "");
              const fieldName = `editorImage-${i}-${j}.${mime}`;
              formData.append(`${fieldName}`, blob, `editor-${i}-${j}.${mime}`);
              img.dataset.placeholder = fieldName;
              img.src = fieldName;
            } else if (img.src.startsWith("data:image/")) {
              const blob = dataURLtoBlob(img.src);
              const mime = blob.type.split("/")[1].replace("+xml", "");
              const fieldName = `editorImage-${i}-${j}.${mime}`;
              formData.append(`${fieldName}`, blob, `editor-${i}-${j}.${mime}`);
              img.dataset.placeholder = fieldName;
              img.src = fieldName;
            } else if (img.src.startsWith("http")) {
              const res = await fetch(img.src);
              const blob = await res.blob();
              const mime = blob.type.split("/")[1].replace("+xml", "");
              const fieldName = `editorImage-${i}-${j}.${mime}`;
              formData.append(`${fieldName}`, blob, `editor-${i}-${j}.${mime}`);
              img.dataset.placeholder = fieldName;
              img.src = fieldName;
            }
          }

          return {
            ...s,
            content: tempDiv.innerHTML,
          };
        })
      );

      formData.append(
        "patch",
        JSON.stringify({
          yamaVersiyonu: newPatch.yamaVersiyonu,
          title: newPatch.title,
          subtitle: newPatch.subtitle,
          summary: newPatch.summary,
          tarih: newPatch.tarih,
          sections: newSections.map((s) => ({
            id: s.id,
            title: s.title,
            content: s.content,
            table: s.table,
            thumbnail: s.thumbnail,
            images: s.images,
          })),
        })
      );

      if (newPatch.imageFile) formData.append("image", newPatch.imageFile);

      newPatch.sections.forEach((s, i) => {
        if (s.imageFile) formData.append(`sectionImage-${i}`, s.imageFile);
        if (s.thumbnailFile)
          formData.append(`sectionThumbnail-${i}`, s.thumbnailFile);
      });

      const res = await fetch(
        `${API_URL}/${language}/addpatch?patchNo=${newPatch.yamaVersiyonu}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("user"))?.token
            }`,
          },
          body: formData,
        }
      );

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || "Yama kaydedilemedi: " + data.message);
        return;
      }

      toast.success(
        `${
          newPatch.yamaVersiyonu
        } No'lu yama ${language.toUpperCase()} dilinde kaydedildi.`
      );
      setHasChange(false);
      navigate("/dashboard");
    } catch (err) {
      toast.error("Sunucu Hatası: " + err);
    }
  };

  return (
    <>
      <div className="sticky z-40 p-2 gap-2 top-0 grid grid-cols-10 grid-rows-auto opacity-0 transition-opacity duration-300 hover:opacity-100 bg-gray-700/30 mr-5">
        {patch.sections?.map((sec, i) => (
          <div
            key={i}
            className="bg-gray-600 m-1 rounded-md text-md text-center cursor-pointer hover:bg-gray-500"
            onClick={() => {
              setTimeout(() => {
                const el = document.getElementById(`${sec.id}`);
                const el2 = document.getElementById(`sec${sec.id}`);
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                if (el2)
                  el2.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
          >
            <span>{i + 1 + ". Bölüm "}</span>
          </div>
        ))}
      </div>
      <div className="flex h-screen overflow-hidden">
        <div
          className="overflow-y-auto border-gray-300"
          style={{ width: "50%" }}
        >
          <PatchForm
            patch={patch}
            setPatch={setPatch}
            setHasChange={setHasChange}
            onSave={savePatch}
            loading={loading}
            setLoading={setLoading}
          />
        </div>

        <div
          onMouseDown={handleMouseDown}
          className="relative gap-6 w-12 flex flex-col justify-center items-center cursor-col-resize group"
        >
          <div className="absolute inset-y-0 w-1 bg-gray-400 opacity-30 group-hover:opacity-60 transition-opacity duration-150 rounded" />
          <div className="relative z-30 w-12 h-12 bg-white/80 rounded-full shadow-lg flex items-center justify-center group-hover:scale-105 transform transition-transform duration-150">
            <BsArrowLeftRight className="text-gray-600 text-2xl" />
          </div>
          <div
            className="cursor-pointer relative z-30 w-12 h-12 bg-white/80 rounded-full shadow-lg flex items-center justify-center group-hover:scale-105 transform transition-transform duration-150"
            onClick={() => setLeftWidth(50)}
          >
            <MdRestartAlt className="text-red-500 text-2xl" />
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <PatchDetails initialPatch={patch} />
        </div>
      </div>
    </>
  );
};

export default AddPatch;